import { SubscribeQuery } from '@/queries';
import { tokens } from 'vue-the-mask';
import { DashboardCardUnit } from '@/models';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
dayjs.locale('pt-br');

function unmask(value: string, mask: string) {
  return maskit(value, mask);
}

function fancyTimeFormat(seconds: number) {
  // Hours, minutes and seconds
  const hrs = ~~(seconds / 3600);
  const mins = ~~((seconds % 3600) / 60);
  const secs = ~~seconds % 60;

  // Output like "1:01" or "4:03:59" or "123:03:59"
  let ret = '';

  if (hrs > 0) {
      ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
  }

  ret += '' + mins + ':' + (secs < 10 ? '0' : '');
  ret += '' + secs;
  return ret;
}

function timeFormat(seconds: number, unit: DashboardCardUnit) {
  let result: number;

  switch (unit) {
    case DashboardCardUnit.SECONDS:
      return seconds;
    case DashboardCardUnit.MINUTES:
      result = seconds / 60;
      break;
    case DashboardCardUnit.HOURS:
      result = seconds / 3600;
      break;
    case DashboardCardUnit.PERCENT:
      result = seconds;
      break;
    case DashboardCardUnit.CURRENCY:
      result = seconds;
      break;
    default:
      result = seconds;
      break;
  }

  return Math.round(result);
}

function percent(value: number, valuePast: number) {
  if (valuePast === undefined || valuePast === 0) {
    return 0;
  }
  if (value > valuePast) {
    return (value - valuePast) / valuePast * 100;
  }
  if (valuePast > value) {
    return (valuePast - value) / valuePast * 100 * -1;
  }
  return 0;
}

function granularityFormat(granularity: number, value: number) {
  switch (granularity) {
    case 6:
      return dayjs(value).format('HH:mm');
    case 7:
      return dayjs(value).format('DD/MM');
    case 10:
      return dayjs(value).format('DD/MM');
    case 12:
      return dayjs(value).format('MMM');
    default:
      return dayjs(value).format('HH:mm');
  }
}

function maskit(value: any, mask: string, masked = false) {
  value = value || '';
  mask = mask || '';
  let iMask = 0;
  let iValue = 0;
  let output = '';
  while (iMask < mask.length && iValue < value.length) {
    let cMask = mask[iMask];
    const masker = tokens[cMask];
    const cValue = value[iValue];
    if (masker && !masker.escape) {
      if (masker.pattern.test(cValue)) {
        output += masker.transform ? masker.transform(cValue) : cValue;
        iMask++;
      }
      iValue++;
    } else {
      if (masker && masker.escape) {
        iMask++; // take the next mask char and treat it as char
        cMask = mask[iMask];
      }
      if (masked) { output += cMask; }
      if (cValue === cMask) { iValue++; } // user typed the same char
      iMask++;
    }
  }

  // fix mask that ends with a char: (#)
  let restOutput = '';
  while (iMask < mask.length && masked) {
    const cMask = mask[iMask];
    if (tokens[cMask]) {
      restOutput = '';
      break;
    }
    restOutput += cMask;
    iMask++;
  }

  return output + restOutput;
}

function stripFilename(file: File) {
  const name = file.name.split('.')[0].replace(/[^\w\s]/gi, '').replace(/ /g, '-');
  const extension = file.name.split('.').pop();
  return `${name}.${extension}`;
}

function buildQueryString(query: SubscribeQuery) {
  let qs = '';
  qs += query.where !== undefined ? `where=${JSON.stringify(query.where)}` : '';
  qs += query.include  !== undefined ? `${qs ? '&' : ''}include=${query.include}` : '';
  qs += query.limit !== undefined ? `${qs ? '&' : ''}limit=${query.limit}` : '';
  qs += query.order  !== undefined ? `${qs ? '&' : ''}order=${query.order}` : '';
  qs += query.skip !== undefined ? `${qs ? '&' : ''}skip=${query.skip}` : '';
  qs += query.count !== undefined ? `${qs ? '&' : ''}count=${query.count}` : '';
  return qs;
}

function buildFullPath(query: SubscribeQuery) {
  return `/parse/classes/${query.className}?${buildQueryString(query)}`;
}

const UtilsService = {
  stripFilename,
  buildQueryString,
  buildFullPath,
  unmask,
  fancyTimeFormat,
  timeFormat,
  percent,
  granularityFormat,
};

export default UtilsService;
